/**
 * CurriculumJourney — Vue « Parcours » d'un curriculum.
 *
 * Affiche les compétences ordonnées avec leur statut (locked / not_started /
 * in_progress / needs_review / mastered), la barre de progression globale,
 * la prochaine étape recommandée et le message de fin si tout est maîtrisé.
 *
 * Props : curriculumId (ex: 'curr_reading_beginner').
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Circle, Lock, Play, AlertTriangle, Sparkles, Trophy,
  ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { learningApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const STATUS_META = {
  mastered:      { fr: 'Maîtrisé',     cls: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
                   icon: CheckCircle2 },
  in_progress:   { fr: 'En cours',     cls: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
                   icon: Play },
  needs_review:  { fr: 'À revoir',     cls: 'text-rose-300 bg-rose-500/15 border-rose-500/30',
                   icon: AlertTriangle },
  not_started:   { fr: 'À commencer',  cls: 'text-text-muted bg-surface-elevated border-border',
                   icon: Circle },
  locked:        { fr: 'Verrouillé',   cls: 'text-text-muted bg-surface-elevated border-border',
                   icon: Lock },
};

const ProgressBar = ({ percent }) => (
  <div className="h-2 w-full bg-background/60 rounded-full overflow-hidden">
    <div
      className="h-full bg-gradient-to-r from-primary/70 to-emerald-400/80 transition-all"
      style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
    />
  </div>
);

const CompetencyRow = ({ competency, isCurrent, onStart, testid }) => {
  const [open, setOpen] = useState(isCurrent);
  const p = competency.progress || {};
  const status = p.status || 'not_started';
  const meta = STATUS_META[status] || STATUS_META.not_started;
  const Icon = meta.icon;
  const locked = !competency.unlocked && status !== 'mastered';
  const isMastered = status === 'mastered';

  const accuracy = Math.round(p.averageScore || 0);
  const dp = p.difficultyProgress || {};
  // Only show difficulty buttons that are NOT empty (no exercises) so the
  // user never clicks into a void.
  const difficulties = ['easy', 'medium', 'hard'].filter(
    (d) => dp[d] && dp[d].status !== 'empty' && (dp[d].total || 0) > 0,
  );

  return (
    <div
      className={`rounded-lg border bg-surface-elevated/30 transition-colors ${
        isMastered ? 'border-emerald-500/60 ring-1 ring-emerald-500/30' :
        isCurrent  ? 'border-primary/50 ring-1 ring-primary/20' :
                     'border-border'
      } ${locked ? 'opacity-70' : ''}`}
      data-testid={testid}
      data-mastered={isMastered ? '1' : '0'}
    >
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
      >
        <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${meta.cls}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium truncate ${isMastered ? 'text-emerald-200' : 'text-text-primary'}`}>
              {competency.title?.fr}
            </span>
            <Badge className={`text-[10px] border ${meta.cls}`}>{meta.fr}</Badge>
            {p.completedSeries > 0 && (
              <span className="text-[11px] text-text-muted">{accuracy}%</span>
            )}
          </div>
          {p.totalPlayableExercises > 0 && (
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 max-w-[180px]">
                <div className="h-1 w-full bg-background/60 rounded overflow-hidden">
                  <div
                    className={`h-full ${isMastered ? 'bg-emerald-400' : 'bg-primary/80'}`}
                    style={{ width: `${Math.round((p.completedExercises / Math.max(p.totalPlayableExercises, 1)) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="text-[11px] text-text-muted" data-testid={`${testid}-counter`}>
                {p.completedExercises}/{p.totalPlayableExercises} ex.
              </span>
            </div>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
      </button>

      {open && (
        <div className="px-4 pb-3 pt-0 space-y-2 border-t border-border/50">
          <p className="text-xs text-text-secondary">{competency.objective?.fr}</p>
          {locked && (
            <p className="text-[11px] text-text-muted" data-testid={`${testid}-locked-msg`}>
              <Lock className="w-3 h-3 inline mr-1" />
              Pour débloquer cette étape, maîtrise d&apos;abord l&apos;étape précédente.
            </p>
          )}
          {!locked && difficulties.length === 0 && (
            <p className="text-[11px] text-text-muted">
              Aucun exercice disponible pour le moment.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {difficulties.map((d) => {
              const block = dp[d];
              const dStatus = block.status; // available | in_progress | needs_reinforcement | mastered | locked
              const dLocked = dStatus === 'locked' || locked;
              const dMastered = dStatus === 'mastered';
              const dInProgress = dStatus === 'in_progress' || dStatus === 'needs_reinforcement';
              const labelFr = d === 'easy' ? 'Facile' : d === 'medium' ? 'Moyen' : 'Difficile';
              return (
                <Button
                  key={d}
                  size="sm"
                  variant={dMastered ? 'default' : (dInProgress ? 'default' : 'outline')}
                  disabled={dLocked}
                  className={
                    dMastered
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700'
                      : ''
                  }
                  onClick={() => !dLocked && onStart(competency, d)}
                  data-testid={`${testid}-start-${d}`}
                  data-difficulty-status={dStatus}
                >
                  {dMastered ? <CheckCircle2 className="w-3 h-3 mr-1" /> :
                   dLocked   ? <Lock         className="w-3 h-3 mr-1" /> :
                               <Play         className="w-3 h-3 mr-1" />}
                  {labelFr}
                  <span className="ml-1.5 text-[10px] opacity-70">
                    {block.completed}/{block.total}
                  </span>
                </Button>
              );
            })}
            {competency.practiceHref && (
              <Link to={competency.practiceHref}>
                <Button size="sm" variant="ghost" className="text-xs">
                  Pratiquer dans le Coran <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RecommendationCard = ({ reco, competencies, onStart }) => {
  const c = competencies.find((x) => x.id === reco.competencyId);
  return (
    <div
      className="rounded-md border border-primary/30 bg-primary/5 p-3 flex items-center gap-3"
      data-testid={`journey-reco-${reco.type}`}
    >
      <Sparkles className="w-4 h-4 text-primary shrink-0" />
      <div className="flex-1 text-sm text-text-primary">
        {reco.reason?.fr}
      </div>
      {c && (
        <Button size="sm" onClick={() => onStart(c, 'easy')}
                data-testid={`journey-reco-cta-${reco.type}`}>
          Commencer
        </Button>
      )}
    </div>
  );
};

export const CurriculumJourney = ({
  curriculumId,
  onStartCompetency,        // (competency, difficulty) → parent triggers exercise series
  testidPrefix = 'journey',
}) => {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [pubData, setPubData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    if (isAuthenticated) {
      learningApi.curriculumProgress(curriculumId)
        .then((r) => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    } else {
      learningApi.getCurriculum(curriculumId)
        .then((r) => setPubData(r.data))
        .catch(() => setPubData(null))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    setLoading(true);
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumId, isAuthenticated]);

  if (loading) {
    return (
      <div className="text-sm text-text-muted p-6" data-testid={`${testidPrefix}-loading`}>
        Chargement du parcours…
      </div>
    );
  }

  // Anonymous: show curriculum overview only
  if (!isAuthenticated) {
    if (!pubData) return null;
    return (
      <div className="space-y-4" data-testid={`${testidPrefix}-anonymous`}>
        <div className="rounded-lg border border-border bg-surface-elevated/30 p-4">
          <h3 className="text-lg font-serif text-text-primary">{pubData.title?.fr}</h3>
          <p className="text-sm text-text-muted mt-1">{pubData.description?.fr}</p>
          <p className="text-[11px] text-text-muted mt-3">
            <Link to="/login" className="text-primary hover:underline">Connectez-vous</Link>
            {' '}pour suivre votre progression à travers les {pubData.competencies?.length} étapes.
          </p>
        </div>
        <div className="space-y-2">
          {pubData.competencies?.map((c, i) => (
            <div key={c.id} className="rounded-md border border-border bg-surface-elevated/20 p-3 flex items-center gap-3"
                 data-testid={`${testidPrefix}-comp-${i}`}>
              <span className="w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center text-xs text-text-muted shrink-0">
                {c.order}
              </span>
              <div className="flex-1">
                <div className="text-sm text-text-primary">{c.title?.fr}</div>
                <div className="text-xs text-text-muted">{c.objective?.fr}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { curriculum, competencies, summary, recommendations } = data;
  const isComplete = summary.status === 'completed';
  const completionMsg = curriculum.completion?.fr;
  const humility = curriculum.humility?.fr;

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-page`}>
      {/* Header + summary */}
      <div className="rounded-lg border border-border bg-surface-elevated/30 p-4 space-y-3"
           data-testid={`${testidPrefix}-summary`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-serif text-text-primary">{curriculum.title?.fr}</h3>
            <p className="text-xs text-text-muted">{curriculum.description?.fr}</p>
          </div>
          <Badge variant="outline" className="text-xs">
            {summary.completedCompetencies}/{summary.totalCompetencies} étapes
          </Badge>
        </div>
        <ProgressBar percent={summary.progressPercent} />
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-muted" data-testid={`${testidPrefix}-percent`}>
            {summary.progressPercent}% du parcours
          </span>
          {summary.currentCompetencyId && (
            <span className="text-primary">
              Étape en cours : « {competencies.find((c) => c.id === summary.currentCompetencyId)?.title?.fr} »
            </span>
          )}
        </div>
      </div>

      {/* Completion banner */}
      {isComplete && completionMsg && (
        <div
          className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-start gap-3"
          data-testid={`${testidPrefix}-completed`}
        >
          <Trophy className="w-6 h-6 text-emerald-300 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-emerald-200 font-medium">Bravo !</h4>
            <p className="text-sm text-emerald-100/80 mt-1">{completionMsg}</p>
            {humility && (
              <p className="text-xs text-emerald-100/60 mt-2 italic">{humility}</p>
            )}
          </div>
        </div>
      )}

      {/* Recommendations (if any) */}
      {!isComplete && recommendations?.length > 0 && (
        <div className="space-y-2" data-testid={`${testidPrefix}-recos`}>
          {recommendations.slice(0, 3).map((r, i) => (
            <RecommendationCard
              key={i}
              reco={r}
              competencies={competencies}
              onStart={onStartCompetency}
            />
          ))}
        </div>
      )}

      {/* Competency timeline */}
      <div className="space-y-2" data-testid={`${testidPrefix}-list`}>
        {competencies.map((c) => (
          <CompetencyRow
            key={c.id}
            competency={c}
            isCurrent={summary.currentCompetencyId === c.id}
            onStart={onStartCompetency}
            testid={`${testidPrefix}-comp-${c.slug}`}
          />
        ))}
      </div>
    </div>
  );
};

export default CurriculumJourney;
